<template>
  <v-main>
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <v-card elevation="6">
            <v-card-title>
              <v-icon class="mr-2">mdi-pump</v-icon>
              Edit Head Pump Size
            </v-card-title>
            <v-card-text>
              <v-form ref="form" v-model="valid" v-if="!isLoading">
                <v-row>
                  <v-col cols="12" md="6">
                    <v-autocomplete
                      class="role-autocomplete"
                      elevation="0"
                      dense
                      solo
                      v-model="form.category"
                      :items="pumpCategoriesFormatted"
                      item-text="text"
                      item-value="value"
                      label="Select Category"
                    ></v-autocomplete>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-autocomplete
                      class="role-autocomplete"
                      elevation="0"
                      dense
                      solo
                      v-model="form.power"
                      :items="motorHPOptionsFormatted"
                      item-text="text"
                      item-value="value"
                      label="Select Power"
                    ></v-autocomplete>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="form.size"
                      type="number"
                      label="Size"
                      outlined
                      dense
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="form.lpd"
                      type="number"
                      label="LPD"
                      outlined
                      dense
                    ></v-text-field>
                  </v-col>
                </v-row>
              </v-form>
              <v-progress-linear
                v-if="isLoading"
                indeterminate
                color="primary"
              ></v-progress-linear>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="grey" text @click="$router.go(-1)">
                Cancel
              </v-btn>
              <v-btn
                color="#6c5dd4"
                dark
                :loading="isSaving"
                :disabled="isSaving"
                @click="update"
              >
                Update
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-main>
</template>

<script>
import axios from "axios";

export default {
  name: "HeadPumpSizeEdit",
  data() {
    return {
      valid: false,
      isLoading: false,
      isSaving: false,
      form: {
        category: "",
        power: "",
        size: null,
        lpd: null,
      },
      pumpCategories: [],
      pumpCategoriesFormatted: [],
      motorHPOptions: [],
      motorHPOptionsFormatted: [],
    };
  },
  async mounted() {
    await this.getPumpCategories();
    await this.getMotorHPOptions();
    await this.getHeadPumpSize();
  },
  methods: {
    async getHeadPumpSize() {
      this.isLoading = true;
      try {
        const headPumpSizeId = this.$route.params.headPumpSizeId;
        let response = await axios.get(`/head_pump_sizes/${headPumpSizeId}`);
        console.log('=== STANDALONE EDIT DEBUG START ===');
        console.log('API Response data:', response.data.data);
        console.log('Category from API:', response.data.data.category);
        console.log('Power from API:', response.data.data.power);
        console.log('Size from API:', response.data.data.size);
        console.log('LPD from API:', response.data.data.lpd);
        console.log('Available categories:', this.pumpCategoriesFormatted);
        console.log('Available power options:', this.motorHPOptionsFormatted);
        
        // Wait for dropdown options to be ready
        await this.$nextTick();
        
        // Set form values directly with strings (not objects)
        // v-autocomplete will match these strings to the dropdown options
        this.$set(this, 'form', {
          category: response.data.data.category || '',
          power: response.data.data.power || '',
          size: response.data.data.size || null,
          lpd: response.data.data.lpd || null,
        });
        
        console.log('Form after setting values:', this.form);
        console.log('=== STANDALONE EDIT DEBUG END ===');
        
        console.log('Form data set to:', this.form);
        
        this.isLoading = false;
      } catch (e) {
        this.isLoading = false;
        this.$vs.notification({
          title: "Error",
          text: "Failed to load Head Pump Size",
          color: "danger",
        });
        console.error(e);
      }
    },
    async update() {
      if (!this.form.category || !this.form.power) {
        this.$vs.notification({
          title: "Validation Error",
          text: "Category and Power are required fields",
          color: "danger",
        });
        return;
      }
      
      try {
        this.isSaving = true;
        const headPumpSizeId = this.$route.params.headPumpSizeId;
        await axios.patch(`/head_pump_sizes/${headPumpSizeId}`, this.form);
        this.isSaving = false;
        this.$vs.notification({
          title: "Success",
          text: "Head Pump Size updated successfully",
          color: "success",
        });
        this.$router.push({ name: "HeadPumpSizesPage" });
      } catch (e) {
        this.isSaving = false;
        this.$vs.notification({
          title: "Error",
          text: "Failed to update Head Pump Size",
          color: "danger",
        });
        console.error(e);
      }
    },
    async getPumpCategories() {
      try {
        let response = await axios.get(`/head_pump_sizes/pump_categories`);
        this.pumpCategories = response.data.data;
        
        // Format for v-autocomplete
        this.pumpCategoriesFormatted = [];
        this.pumpCategories.forEach((category) => {
          this.pumpCategoriesFormatted.push({
            id: category.id,
            text: category.description,
            value: category.description,
          });
        });
      } catch (e) {
        console.error('Error fetching pump categories:', e);
        this.$vs.notification({
          title: "Error",
          text: "Failed to load pump categories",
          color: "danger",
        });
      }
    },
    async getMotorHPOptions() {
      try {
        let response = await axios.get(`/head_pump_sizes/motor_hp`);
        this.motorHPOptions = response.data.data;
        
        // Format for v-autocomplete
        this.motorHPOptionsFormatted = [];
        this.motorHPOptions.forEach((power) => {
          this.motorHPOptionsFormatted.push({
            id: power.id,
            text: power.description,
            value: power.description,
          });
        });
      } catch (e) {
        console.error('Error fetching motor HP options:', e);
        this.$vs.notification({
          title: "Error",
          text: "Failed to load motor HP options",
          color: "danger",
        });
      }
    },
  },
};
</script>

<style scoped>
.role-autocomplete .v-input__slot {
  box-shadow: none !important;
  background: #f4f7f8 !important;
  padding-bottom: 10px !important;
  padding-top: 10px !important;
  border-radius: 10px;
}
</style>